/* GASSISA
=========================================================================================
* Descripcion: Lector de codigos de barras por camara para dispositivos portatiles.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion
 *      2       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Escaneo continuo sin detener camara
 *      3       | 08/10/2026 | LIC. José Luis Aragón C.                       | Motor ZXing fallback para iOS
 *              |            |                                  | (sin BarcodeDetector): escaneo
 *              |            |                                  | continuo + captura puntual;
 *              |            |                                  | expone toggle por ref para icono
 *              |            |                                  | junto al lector manual
 * ---------------------------------------------------------------------------------------
 */

import { forwardRef, useRef, useState, useEffect, useImperativeHandle } from "react";
import { CircularProgress, Chip, IconButton } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useVentas } from "../../../context/Ventas/VentasContext";
import { useSesion } from "../../../context/System/SesionContext";

const normalizarCodigo = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase();

const DEBOUNCE_MS = 800;
// Formatos 1D de mercancía (mismos que el detector nativo).
const FORMATOS_ZXING = ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf"];

const SizeSoftXCodigoBarrasCamaraPortatil = forwardRef(function SizeSoftXCodigoBarrasCamaraPortatil(
  { TipoVenta = "PREVENTA" },
  ref
) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanAnimationRef = useRef(null);
  const scanBusyRef = useRef(false);
  const ultimoCodigoRef = useRef("");
  const ultimoTimestampRef = useRef(0);
  // Rev 3: lector ZXing para iOS/Safari (sin BarcodeDetector nativo).
  const zxingReaderRef = useRef(null);
  const zxingActivoRef = useRef(false);

  const [camaraActiva, setCamaraActiva] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [ultimoCodigo, setUltimoCodigo] = useState("");
  const [capturando, setCapturando] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [barcodeDetectorSoportado, setBarcodeDetectorSoportado] = useState("BarcodeDetector" in window);
  // Rev 3: motor real en uso ("nativo" o "zxing"). En iOS el nativo no existe
  // y se usa ZXing para que el escaneo continuo sí funcione.
  const [motorLectura, setMotorLectura] = useState(null);

  const { parametrosCajaForm } = useSesion();
  const {
    handleRegistrarArticuloSubmitPreVenta,
    handleRegistrarArticuloSubmitVenta,
    handleAbrirValidarCliente,
    barcodeAnterior,
  } = useVentas();

  const getErrorMessage = (err) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return "Este navegador no permite abrir la cámara desde la web.";
    }
    if (!window.isSecureContext) {
      return "La cámara requiere HTTPS o localhost.";
    }
    if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
      return "Permiso de cámara denegado.";
    }
    if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
      return "No se encontró una cámara disponible.";
    }
    if (err?.name === "NotReadableError" || err?.name === "TrackStartError") {
      return "La cámara está ocupada por otra aplicación.";
    }
    return "No se pudo iniciar la cámara.";
  };

  const detenerCamara = () => {
    if (scanAnimationRef.current) {
      cancelAnimationFrame(scanAnimationRef.current);
      scanAnimationRef.current = null;
    }
    // Rev 3: detener también el bucle ZXing (iOS) si está activo.
    try { zxingReaderRef.current?.reset?.(); } catch {}
    zxingReaderRef.current = null;
    zxingActivoRef.current = false;
    setMotorLectura(null);
    const activeStream = streamRef.current || videoRef.current?.srcObject;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    detectorRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    ultimoCodigoRef.current = "";
    setCameraStatus("idle");
  };

  // Rev 3: motor ZXing para iOS/Safari (sin BarcodeDetector). Import
  // dinámico para no inflar el bundle inicial; decodifica 1D en continuo
  // directo del video, igual que el escaneo nativo en Android/Chrome.
  const iniciarCamaraZxing = async () => {
    const { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } = await import("@zxing/browser");
    const hints = new Map();
    const porNombre = {
      code_128: BarcodeFormat.CODE_128,
      code_39: BarcodeFormat.CODE_39,
      ean_13: BarcodeFormat.EAN_13,
      ean_8: BarcodeFormat.EAN_8,
      upc_a: BarcodeFormat.UPC_A,
      upc_e: BarcodeFormat.UPC_E,
      itf: BarcodeFormat.ITF,
    };
    hints.set(
      DecodeHintType.POSSIBLE_FORMATS,
      FORMATOS_ZXING.map((f) => porNombre[f]).filter(Boolean)
    );
    const reader = new BrowserMultiFormatReader(hints, DEBOUNCE_MS);
    zxingReaderRef.current = reader;
    zxingActivoRef.current = true;
    await reader.decodeFromConstraints(
      {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      },
      videoRef.current,
      (result, error) => {
        try {
          const codigoCrudo = result?.getText?.()?.trim?.();
          if (!codigoCrudo) return;
          const ahora = Date.now();
          if (codigoCrudo === ultimoCodigoRef.current && ahora - ultimoTimestampRef.current <= DEBOUNCE_MS) return;
          ultimoCodigoRef.current = codigoCrudo;
          ultimoTimestampRef.current = ahora;
          setUltimoCodigo(codigoCrudo);
          procesarCodigo(codigoCrudo);
        } catch {}
      }
    );
    if (videoRef.current) {
      streamRef.current = videoRef.current.srcObject || streamRef.current;
    }
    setMotorLectura("zxing");
  };

  const iniciarCamara = async () => {
    try {
      setError(null);
      setCameraStatus("requesting");

      if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
        throw new Error("CAMERA_UNAVAILABLE");
      }

      detenerCamara();

      // Rev 3: en iOS (sin BarcodeDetector) el escaneo nativo no existe;
      // ZXing decodifica en continuo para que la cámara sí lea códigos.
      if (!("BarcodeDetector" in window)) {
        if (!videoRef.current) throw new Error("CAMERA_UNAVAILABLE");
        await iniciarCamaraZxing();
        setCameraStatus("ready");
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch(() => {});
      }

      setCameraStatus("ready");
      setMotorLectura("nativo");
      iniciarEscaneo();
    } catch (err) {
      detenerCamara();
      setCameraStatus("error");
      setError(getErrorMessage(err));
    }
  };

  const registrarCodigo = async (codigoBarras) => {
    if (TipoVenta === "VENTA") {
      await handleRegistrarArticuloSubmitVenta(codigoBarras, parametrosCajaForm.dominio);
    } else {
      await handleRegistrarArticuloSubmitPreVenta(codigoBarras, parametrosCajaForm.dominio);
    }
  };

  const procesarCodigo = async (codigoCrudo) => {
    const codigoBarras = normalizarCodigo(codigoCrudo);
    if (!codigoBarras) return;

    if (codigoBarras.includes("+")) {
      const repeticiones = Number.parseInt(codigoBarras.replace("+", ""), 10);
      if (barcodeAnterior?.length >= 5 && Number.isInteger(repeticiones) && repeticiones > 0) {
        for (let index = 0; index < repeticiones; index += 1) {
          await registrarCodigo(barcodeAnterior);
        }
      }
      return;
    }

    if (codigoBarras.includes(".'LAUNION'.")) {
      handleAbrirValidarCliente();
      return;
    }

    await registrarCodigo(codigoBarras);
  };

  const formatosPreferidos = ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf"];

  const prepararDetector = async () => {
    if (!("BarcodeDetector" in window)) return null;
    if (!detectorRef.current) {
      try {
        const formatos = await window.BarcodeDetector.getSupportedFormats?.();
        const soportados = formatos?.filter((f) => formatosPreferidos.includes(f)) || formatosPreferidos;
        detectorRef.current = new window.BarcodeDetector({ formats: soportados });
      } catch {
        detectorRef.current = new window.BarcodeDetector();
      }
    }
    return detectorRef.current;
  };

  const capturarFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    return canvas;
  };

  const handleCapturarManual = async () => {
    if (capturando || !videoRef.current) return;
    setCapturando(true);
    try {
      // Rev 3: con ZXing activo (iOS) el escaneo ya es continuo; la captura
      // puntual fuerza una decodificación inmediata del frame actual.
      if (zxingActivoRef.current && zxingReaderRef.current) {
        try {
          const resultado = await zxingReaderRef.current.decodeOnceFromVideoElement(videoRef.current);
          const codigoCrudo = resultado?.getText?.()?.trim?.();
          if (codigoCrudo) {
            setUltimoCodigo(codigoCrudo);
            await procesarCodigo(codigoCrudo);
            return;
          }
        } catch {}
      }
      if (barcodeDetectorSoportado) {
        let codigoCrudo = null;
        const detector = await prepararDetector();
        if (detector) {
          const resultados = await detector.detect(videoRef.current);
          codigoCrudo = resultados?.[0]?.rawValue?.trim();
        }
        if (!codigoCrudo) {
          const canvas = capturarFrame();
          if (canvas && detector) {
            const resultados = await detector.detect(canvas);
            codigoCrudo = resultados?.[0]?.rawValue?.trim();
          }
        }
        if (codigoCrudo) {
          setUltimoCodigo(codigoCrudo);
          await procesarCodigo(codigoCrudo);
          return;
        }
      } else {
        const video = videoRef.current;
        const wasPaused = video.paused;
        if (!wasPaused) video.pause();
        const canvas = capturarFrame();
        if (canvas) {
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          setSnapshotUrl(dataUrl);
          setTimeout(() => setSnapshotUrl(null), 1500);
        }
        if (!wasPaused) video.play().catch(() => {});
      }
    } finally {
      setCapturando(false);
    }
  };

  const iniciarEscaneo = () => {
    if (!barcodeDetectorSoportado) return;

    let cancelado = false;

    const leerFrame = async () => {
      if (cancelado || scanBusyRef.current) {
        if (!cancelado) {
          scanAnimationRef.current = requestAnimationFrame(leerFrame);
        }
        return;
      }

      const detector = await prepararDetector();
      if (!detector || !videoRef.current || cancelado) return;

      try {
        scanBusyRef.current = true;
        const resultados = await detector.detect(videoRef.current);
        const codigoCrudo = resultados?.[0]?.rawValue?.trim();
        const ahora = Date.now();

        if (codigoCrudo && (codigoCrudo !== ultimoCodigoRef.current || ahora - ultimoTimestampRef.current > DEBOUNCE_MS)) {
          ultimoCodigoRef.current = codigoCrudo;
          ultimoTimestampRef.current = ahora;
          setUltimoCodigo(codigoCrudo);
          await procesarCodigo(codigoCrudo);
        }
      } catch {
        // ignore frame errors
      } finally {
        scanBusyRef.current = false;
      }

      if (!cancelado) {
        scanAnimationRef.current = requestAnimationFrame(leerFrame);
      }
    };

    scanAnimationRef.current = requestAnimationFrame(leerFrame);
  };

  useEffect(() => {
    if (!camaraActiva) {
      detenerCamara();
    }
  }, [camaraActiva]);

  useEffect(() => {
    return () => {
      detenerCamara();
    };
  }, []);

  const toggleCamara = () => {
    if (camaraActiva) {
      detenerCamara();
      setCamaraActiva(false);
    } else {
      setCamaraActiva(true);
      iniciarCamara();
    }
  };

  // Rev 3: control externo (icono cámara junto al lector manual).
  useImperativeHandle(ref, () => ({
    toggleCamara,
    abrirCamara: () => { if (!camaraActiva) toggleCamara(); },
    cerrarCamara: () => { if (camaraActiva) toggleCamara(); },
    camaraActiva: () => camaraActiva,
  }));

  return (
    <div className="camara-codigo-barras-portatil">
      <button
        className="camara-codigo-barras-portatil__toggle"
        onClick={toggleCamara}
        disabled={cameraStatus === "requesting"}
      >
        {camaraActiva ? <VideocamOffIcon /> : <VideocamIcon />}
        <span>{camaraActiva ? "Cerrar cámara" : "Escanear con cámara"}</span>
      </button>

      {camaraActiva && (
        <div className="camara-codigo-barras-portatil__visor">
          {cameraStatus === "requesting" && (
            <div className="camara-codigo-barras-portatil__overlay">
              <CircularProgress size={24} sx={{ color: "white" }} />
              <span>Iniciando cámara...</span>
            </div>
          )}

          {cameraStatus === "error" && (
            <div className="camara-codigo-barras-portatil__overlay">
              <span className="camara-codigo-barras-portatil__error">{error}</span>
              <button className="camara-codigo-barras-portatil__retry" onClick={iniciarCamara}>
                Reintentar
              </button>
            </div>
          )}

          <div className="camara-codigo-barras-portatil__video-wrapper">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onClick={handleCapturarManual}
              className={`camara-codigo-barras-portatil__video ${cameraStatus === "ready" && !motorLectura ? "camara-codigo-barras-portatil__video--tappable" : ""}`}
              style={{ cursor: cameraStatus === "ready" ? "pointer" : "default" }}
            />
            {cameraStatus === "ready" && !motorLectura && (
              <div className="camara-codigo-barras-portatil__tap-hint">
                <CameraAltIcon fontSize="small" />
                <span>Toca la pantalla para escanear</span>
              </div>
            )}
            {snapshotUrl && (
              <div className="camara-codigo-barras-portatil__snapshot">
                <img src={snapshotUrl} alt="captura" className="camara-codigo-barras-portatil__snapshot-img" />
              </div>
            )}
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {cameraStatus === "ready" && (
            <>
              {motorLectura && (
                <Chip
                  size="small"
                  color="success"
                  label={motorLectura === "zxing" ? "Escaneo continuo... (iOS)" : "Escaneo continuo..."}
                  className="camara-codigo-barras-portatil__status"
                />
              )}
              {!motorLectura && (
                <Chip
                  size="small"
                  color="warning"
                  label="Toca la pantalla para capturar"
                  className="camara-codigo-barras-portatil__status"
                />
              )}
              {ultimoCodigo && (
                <Chip
                  size="small"
                  color="info"
                  label={ultimoCodigo}
                  className="camara-codigo-barras-portatil__ultimo"
                />
              )}
              <IconButton
                className="camara-codigo-barras-portatil__capturar"
                size="small"
                onClick={handleCapturarManual}
                disabled={capturando}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default SizeSoftXCodigoBarrasCamaraPortatil;
